import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { UNSAFE_NavigationContext, useNavigate } from "react-router-dom";
import {
	Box,
	Breadcrumbs,
	Button,
	Grid,
	IconButton,
	LinearProgress,
	Paper,
	Typography,
	Link,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Stack,
	InputBase,
	Tooltip,
	Dialog,
	Fab,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
} from "@mui/material";
import {
	UploadFile,
	DriveFolderUpload,
	Folder as FolderIcon,
	Home as HomeIcon,
	MoreVert as MoreVertIcon,
	Share as ShareIcon,
	Movie as MovieIcon,
	Description as DescriptionIcon,
	NavigateNext as NavigateNextIcon,
	Download as DownloadIcon,
	Search as SearchIcon,
	Delete,
	CreateNewFolder,
	ArrowBackRounded,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { GlobalState } from "context/GlobalContext";
import { rootProtectedPath } from "data/authData";
import {
	DEFAULT_BATCH_CONCURRENCY,
	DEFAULT_PAGE_SIZE,
	getVmWorkspaceSignedUrl,
	getVmWorkspaceSignedUrlsBatch,
	listVmWorkspaceContents,
	registerVmWorkspaceFiles,
	searchVmWorkspaceContents,
} from "./services";

// Styled components
const DropZone = styled(Box, {
	shouldForwardProp: (prop) => prop !== "isDragOver",
})(({ theme, isDragOver }) => ({
	flexGrow: 1,
	minHeight: "400px",
	border: isDragOver ? `2px dashed ${theme.palette.primary.main}` : "2px dashed transparent",
	backgroundColor: isDragOver ? theme.palette.action.hover : "transparent",
	transition: "all 0.2s ease-in-out",
	borderRadius: theme.shape.borderRadius,
	padding: theme.spacing(2),
	display: "flex",
	flexDirection: "column",
}));

const FileCard = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(1),
	display: "flex",
	justifyContent: "space-around",
	flexDirection: "column",
	alignItems: "center",
	cursor: "pointer",
	position: "relative",
	// overflow: "hidden",
	"&:hover": {
		backgroundColor: theme.palette.action.hover,
		"& .more-actions": {
			opacity: 1,
		},
	},
	height: "80%",
	width: "80%",
}));

const PreviewBox = styled(Box)({
	width: "100%",
	height: "80px",
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	marginBottom: "8px",
	overflow: "hidden",
	borderRadius: "4px",
	backgroundColor: "#f5f5f5",
});

const PreviewImage = styled("img")({
	width: "100%",
	height: "100%",
	objectFit: "cover",
});

const formatBytes = (bytes, decimals = 2) => {
	if (!+bytes) return "0 Bytes";
	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const traverseFileTree = (item) => {
	return new Promise((resolve) => {
		if (item.isFile) {
			item.file((file) => {
				file.customPath = item.fullPath.substring(1);
				resolve([file]);
			});
		} else if (item.isDirectory) {
			const dirReader = item.createReader();
			const entries = [];

			const readEntries = () => {
				dirReader.readEntries(async (result) => {
					if (result.length === 0) {
						const promises = entries.map((entry) => traverseFileTree(entry));
						const nestedFiles = await Promise.all(promises);
						resolve(nestedFiles.flat());
					} else {
						entries.push(...result);
						readEntries();
					}
				});
			};
			readEntries();
		}
	});
};

function useBlocker(blocker, when = true) {
	const { navigator } = useContext(UNSAFE_NavigationContext);

	useEffect(() => {
		if (!when) return;

		if (!navigator || typeof navigator.block !== "function") return;

		const unblock = navigator.block((tx) => {
			const autoUnblockingTx = {
				...tx,
				retry() {
					unblock();
					tx.retry();
				},
			};

			blocker(autoUnblockingTx);
		});

		return unblock;
	}, [navigator, blocker, when]);
}

const VmWorkspace = () => {
	const navigate = useNavigate();
	const [path, setPath] = useState([]);

	const handleGoBack = () => {
		if (!path?.length) {
			navigate(`/${rootProtectedPath}`);
		} else {
			setPath((prev) => prev.slice(0, -1));
		}
	};
	const [folderItems, setFolderItems] = useState([]);
	const [objectItems, setObjectItems] = useState([]);
	const [nextCursor, setNextCursor] = useState(null);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [isInitialLoading, setIsInitialLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isFetchingSignedUrls, setIsFetchingSignedUrls] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [isSearchMode, setIsSearchMode] = useState(false);
	const [totalMatches, setTotalMatches] = useState(0);
	const [error, setError] = useState("");
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [uploadStats, setUploadStats] = useState({
		speed: 0,
		uploaded: 0,
		total: 0,
		currentFile: "",
	});
	const [dragOver, setDragOver] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedItem, setSelectedItem] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
	const [blockedTx, setBlockedTx] = useState(null);
	const [binDragOver, setBinDragOver] = useState(false);
	const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
	const [newFolderName, setNewFolderName] = useState("");
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	const fileInputRef = useRef(null);
	const folderInputRef = useRef(null);
	const currentUploadProgress = useRef(0);
	const lastUploadStats = useRef({ time: 0, loaded: 0 });
	const pathRequestRef = useRef("");
	const latestSearchRequestRef = useRef(0);
	const signedUrlCacheRef = useRef(new Map());

	const globalUploadState = useRef({
		totalBytes: 0,
		files: new Map(),
		activeCount: 0,
	});

	const isPreviewableObject = (obj) => {
		const contentType = (obj.contentType || "").toLowerCase();
		if (contentType.startsWith("image/") || contentType.startsWith("video/")) return true;
		const ext = (obj.name || "").split(".").pop().toLowerCase();
		return ["jpg", "jpeg", "png", "gif", "webp", "mp4", "webm"].includes(ext);
	};

	const mapFolderItems = (folders, currentPath) =>
		(folders || []).filter((f) => f !== "TRASH").map((f, i) => ({
			id: `folder-${currentPath || "root"}-${f}-${i}`,
			name: f,
			type: "folder",
			fullPath: currentPath ? `${currentPath}/${f}` : f,
		}));

	const mapObjectItems = (objects, currentPath, offset = 0) =>
		(objects || []).map((obj, i) => ({
			id: `file-${currentPath || "root"}-${obj.fileName || obj.name}-${offset + i}`,
			name: obj.name,
			type: "file",
			mimeType: obj.contentType || "application/octet-stream",
			url: null,
			fullPath: currentPath ? `${currentPath}/${obj.name}` : obj.name,
			fileName: obj.fileName || (currentPath ? `${currentPath}/${obj.name}` : obj.name),
			size: obj.size,
			timeCreated: obj.timeCreated,
			updated: obj.updated,
			contentType: obj.contentType,
		}));

	const hydrateSignedUrlsForObjects = useCallback(
		async (items) => {
			const previewableItems = items.filter((obj) => isPreviewableObject(obj));
			if (!previewableItems.length) return;

			const missingFileNames = previewableItems
				.map((obj) => obj.fileName)
				.filter((fileName) => fileName && !signedUrlCacheRef.current.has(fileName));
			if (missingFileNames.length) {
				setIsFetchingSignedUrls(true);
				try {
					const batchMap = await getVmWorkspaceSignedUrlsBatch({
						fileNames: missingFileNames,
						download: false,
						concurrency: DEFAULT_BATCH_CONCURRENCY,
					});
					batchMap.forEach((url, fileName) => signedUrlCacheRef.current.set(fileName, url));

					// Fallback for any missing batch entries.
					const fallbackTargets = missingFileNames.filter((fileName) => !signedUrlCacheRef.current.has(fileName));
					if (fallbackTargets.length) {
						await Promise.all(
							fallbackTargets.map(async (fileName) => {
								try {
									const url = await getVmWorkspaceSignedUrl({ fileName, download: false });
									if (url) signedUrlCacheRef.current.set(fileName, url);
								} catch (signedUrlError) {
									console.warn("Signed URL fallback failed", signedUrlError);
								}
							}),
						);
					}
				} finally {
					setIsFetchingSignedUrls(false);
				}
			}

			setObjectItems((prev) =>
				prev.map((item) => {
					if (!isPreviewableObject(item)) return item;
					const cachedUrl = signedUrlCacheRef.current.get(item.fileName);
					if (!cachedUrl || item.url === cachedUrl) return item;
					return { ...item, url: cachedUrl };
				}),
			);
		},
		[setObjectItems],
	);

	const fetchInitialContents = useCallback(async () => {
		const currentPath = path.join("/");
		pathRequestRef.current = currentPath;
		setIsInitialLoading(true);
		setError("");
		setFolderItems([]);
		setObjectItems([]);
		setNextCursor(null);
		setHasNextPage(false);
		setLoadingOpen(true);
		try {
			const data = await listVmWorkspaceContents({ path: currentPath, pageSize: DEFAULT_PAGE_SIZE });
			if (pathRequestRef.current !== currentPath) return;
			const folders = mapFolderItems(data.folders, currentPath);
			const objects = mapObjectItems(data.objects, currentPath, 0);
			setFolderItems(folders);
			setObjectItems(objects);
			const newNextCursor = data.pagination?.nextCursor ?? null;
			setNextCursor(newNextCursor);
			setHasNextPage(Boolean(data.pagination?.hasNextPage ?? newNextCursor));
			await hydrateSignedUrlsForObjects(objects);
		} catch (fetchError) {
			console.error("Failed to fetch contents", fetchError);
			setError("Failed to load files. Please try again.");
			setSnackBarOpen(true);
			setSnackBar((prev) => ({ ...prev, message: "Failed to load files", severity: "error" }));
		} finally {
			setIsInitialLoading(false);
			setLoadingOpen(false);
		}
	}, [hydrateSignedUrlsForObjects, path, setLoadingOpen, setSnackBar, setSnackBarOpen]);

	const handleLoadMore = async () => {
		if (isLoadingMore || !nextCursor) return;

		const currentPath = path.join("/");
		setIsLoadingMore(true);
		setError("");
		try {
			const data = await listVmWorkspaceContents({
				path: currentPath,
				pageSize: DEFAULT_PAGE_SIZE,
				cursor: nextCursor,
			});

			const newObjects = mapObjectItems(data.objects, currentPath, objectItems.length);
			setObjectItems((prev) => [...prev, ...newObjects]);
			const newNextCursor = data.pagination?.nextCursor ?? null;
			setNextCursor(newNextCursor);
			setHasNextPage(Boolean(data.pagination?.hasNextPage ?? newNextCursor));
			await hydrateSignedUrlsForObjects(newObjects);
		} catch (fetchError) {
			console.error("Failed to load more contents", fetchError);
			setError("Failed to load more files. Please retry.");
			setSnackBarOpen(true);
			setSnackBar((prev) => ({ ...prev, message: "Failed to load more files", severity: "error" }));
		} finally {
			setIsLoadingMore(false);
		}
	};

	const executeSearch = useCallback(
		async (rawSearchValue) => {
			const searchValue = rawSearchValue.trim();
			if (!searchValue) return;

			const currentPath = path.join("/");
			const requestId = latestSearchRequestRef.current + 1;
			latestSearchRequestRef.current = requestId;

			setIsSearching(true);
			setError("");
			try {
				const data = await searchVmWorkspaceContents({
					path: currentPath,
					search: searchValue,
				});
				if (latestSearchRequestRef.current !== requestId) return;

				const folders = mapFolderItems(data.folders, currentPath);
				const objects = mapObjectItems(data.objects, currentPath, 0);
				setIsSearchMode(true);
				setFolderItems(folders);
				setObjectItems(objects);
				setNextCursor(null);
				setHasNextPage(false);
				setTotalMatches(Number(data.totalMatches ?? folders.length + objects.length));
				await hydrateSignedUrlsForObjects(objects);
			} catch (searchError) {
				if (latestSearchRequestRef.current !== requestId) return;
				console.error("Search failed", searchError);
				setError("Failed to search files. Please try again.");
				setSnackBarOpen(true);
				setSnackBar((prev) => ({ ...prev, message: "Search failed", severity: "error" }));
			} finally {
				if (latestSearchRequestRef.current === requestId) {
					setIsSearching(false);
				}
			}
		},
		[hydrateSignedUrlsForObjects, path, setSnackBar, setSnackBarOpen],
	);

	const clearSearchMode = useCallback(async () => {
		latestSearchRequestRef.current += 1;
		setIsSearchMode(false);
		setTotalMatches(0);
		setError("");
		setIsSearching(false);
		await fetchInitialContents();
	}, [fetchInitialContents]);

	const handleSearchSubmit = async () => {
		const trimmedSearch = searchQuery.trim();
		if (!trimmedSearch) {
			if (isSearchMode) {
				await clearSearchMode();
			}
			return;
		}
		await executeSearch(trimmedSearch);
	};

	useEffect(() => {
		// Keep cache scoped to current path context.
		signedUrlCacheRef.current = new Map();
		latestSearchRequestRef.current += 1;
		setIsSearchMode(false);
		setTotalMatches(0);
		fetchInitialContents();
	}, [fetchInitialContents]);

	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (uploading) {
				e.preventDefault();
				e.returnValue = "";
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [uploading]);

	useBlocker((tx) => {
		setBlockedTx(tx);
		setConfirmationDialogOpen(true);
	}, uploading);

	const handleDragOver = (e) => {
		e.preventDefault();
		setDragOver(true);
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		setDragOver(false);
	};

	const handleDrop = async (e) => {
		e.preventDefault();
		setDragOver(false);

		const items = e.dataTransfer.items;
		if (items) {
			const queue = [];
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				if (item.kind === "file") {
					const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
					if (entry) {
						queue.push(traverseFileTree(entry));
					}
				}
			}
			const results = await Promise.all(queue);
			handleUploadFiles(results.flat());
		} else {
			const droppedFiles = Array.from(e.dataTransfer.files);
			handleUploadFiles(droppedFiles);
		}
	};

	const handleUploadFiles = async (fileList) => {
		if (!fileList || fileList.length === 0) return;

		const newFiles = Array.from(fileList);
		const newFilesTotalSize = newFiles.reduce((acc, file) => acc + file.size, 0);

		// Update global state
		globalUploadState.current.totalBytes += newFilesTotalSize;
		globalUploadState.current.activeCount += newFiles.length;
		newFiles.forEach((f) => globalUploadState.current.files.set(f, 0));

		if (!uploading) {
			lastUploadStats.current = { time: Date.now(), loaded: 0 };
		}

		setUploading(true);

		const updateGlobalProgress = (fileName = "") => {
			let loaded = 0;
			for (const val of globalUploadState.current.files.values()) {
				loaded += val;
			}
			const totalSize = globalUploadState.current.totalBytes;
			const percent = totalSize > 0 ? Math.round((loaded / totalSize) * 100) : 0;

			const now = Date.now();
			if (now - lastUploadStats.current.time >= 500 || percent === 100) {
				const timeDiff = (now - lastUploadStats.current.time) / 1000;
				const loadedDiff = loaded - lastUploadStats.current.loaded;
				const speed = timeDiff > 0 ? loadedDiff / timeDiff : 0;

				setUploadStats({
					speed,
					uploaded: loaded,
					total: totalSize,
					currentFile: fileName,
				});

				lastUploadStats.current = { time: now, loaded };
			}

			// Only update state if percentage changed to avoid re-renders
			if (percent !== currentUploadProgress.current) {
				currentUploadProgress.current = percent;
				setUploadProgress(percent);
			}
		};

		// Initial update to reflect new total size
		updateGlobalProgress();

		// Pre-calculate paths and content types
		const filesData = newFiles.map((file) => {
			const currentPath = path.join("/");
			let filePath = file.name;
			if (file.customPath) {
				filePath = file.customPath;
			} else if (file.webkitRelativePath) {
				filePath = file.webkitRelativePath;
			}
			if (currentPath) {
				filePath = `${currentPath}/${filePath}`;
			}
			return {
				file,
				fileName: filePath,
				contentType: file.type || "application/octet-stream",
			};
		});

		// Batch fetch upload URLs
		const urlMap = new Map();
		const BATCH_SIZE = 50;
		const urlBatches = [];
		for (let i = 0; i < filesData.length; i += BATCH_SIZE) {
			urlBatches.push(filesData.slice(i, i + BATCH_SIZE));
		}

		await Promise.all(
			urlBatches.map(async (batch) => {
				try {
					const { data } = await axios.post("/api/vm_workspace/resumable_upload_url", {
						files: batch.map((f) => ({ fileName: f.fileName, contentType: f.contentType })),
					});
					data.urls.forEach((u) => urlMap.set(u.fileName, u.url));
				} catch (e) {
					console.error("Failed to fetch upload URLs batch", e);
				}
			}),
		);

		const uploadedFiles = [];
		const uploadFile = async (fileData) => {
			const { file, fileName, contentType } = fileData;
			const uploadUrl = urlMap.get(fileName);

			if (!uploadUrl) {
				console.error(`No upload URL for ${fileName}`);
				return;
			}

			try {
				await new Promise((resolve, reject) => {
					const xhr = new XMLHttpRequest();
					xhr.open("PUT", uploadUrl);
					xhr.setRequestHeader("Content-Type", contentType);

					let lastUpdate = 0;
					xhr.upload.onprogress = (event) => {
						if (event.lengthComputable) {
							globalUploadState.current.files.set(file, event.loaded);
							const now = Date.now();
							if (now - lastUpdate > 200) {
								updateGlobalProgress(file.name);
								lastUpdate = now;
							}
						}
					};

					xhr.onload = () => {
						if (xhr.status >= 200 && xhr.status < 300) {
							globalUploadState.current.files.set(file, file.size);
							updateGlobalProgress(file.name);
							resolve();
						} else {
							reject(new Error(`Upload failed: ${xhr.status} ${xhr.responseText}`));
						}
					};

					xhr.onerror = () => reject(new Error("Network Error"));
					xhr.send(file);
				});
				uploadedFiles.push({
					fileName,
					contentType,
					size: file.size,
				});
				console.log(`Uploaded: ${fileName}`);
			} catch (e) {
				console.error("Upload failed", e);
				let errMsg = `Upload failed for ${file.name}`;
				if (e.response?.data?.message) {
					errMsg += `: ${e.response.data.message}`;
				} else if (e.message) {
					errMsg += `: ${e.message}`;
				}
				setSnackBar({ ...snackBar, message: errMsg, severity: "error" });
				setSnackBarOpen(true);
			} finally {
				globalUploadState.current.activeCount--;
			}
		};

		const CONCURRENCY = 6; // Increased concurrency
		const queue = [...filesData];
		const workers = [];

		for (let i = 0; i < CONCURRENCY; i++) {
			workers.push(
				(async () => {
					while (queue.length > 0) {
						const fileData = queue.shift();
						if (fileData) await uploadFile(fileData);
					}
				})(),
			);
		}

		await Promise.all(workers);
		if (uploadedFiles.length) {
			try {
				await registerVmWorkspaceFiles({ files: uploadedFiles });
			} catch (indexError) {
				console.error("Failed to index uploaded files", indexError);
			}
		}
		await fetchInitialContents();

		if (globalUploadState.current.activeCount === 0) {
			setUploading(false);
			setUploadProgress(0);
			globalUploadState.current = {
				totalBytes: 0,
				files: new Map(),
				activeCount: 0,
			};
			if (fileInputRef.current) fileInputRef.current.value = "";
			if (folderInputRef.current) folderInputRef.current.value = "";
		}
	};

	const handleCreateFolder = async () => {
		if (!newFolderName.trim()) return;
		const currentPath = path.join("/");
		const folderPath = currentPath ? `${currentPath}/${newFolderName}` : newFolderName;

		setLoadingOpen(true);
		try {
			await axios.get("/api/vm_workspace/create_folder", {
				params: { folderPath },
			});
			setSnackBar({ ...snackBar, message: "Folder created successfully", severity: "success" });
			setSnackBarOpen(true);
			setCreateFolderDialogOpen(false);
			setNewFolderName("");
			await fetchInitialContents();
		} catch (error) {
			console.error("Failed to create folder", error);
			setSnackBar({ ...snackBar, message: "Failed to create folder", severity: "error" });
			setSnackBarOpen(true);
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleNavigate = (folderName) => {
		setPath([...path, folderName]);
	};

	const handleBreadcrumbClick = (index) => {
		setPath(path.slice(0, index + 1));
	};

	const handleHomeClick = () => {
		setPath([]);
	};

	const handleFileClick = async (file, isDownload = false) => {
		if (file.type === "folder") {
			handleNavigate(file.name);
			return;
		}

		// Open window immediately for view (not download) to avoid popup blockers
		let newWindow = null;
		if (!isDownload) {
			newWindow = window.open("", "_blank");
			if (newWindow) newWindow.document.write("Loading...");
		}

		try {
			const fullPath = file.fileName || file.fullPath;
			const url = await getVmWorkspaceSignedUrl({ fileName: fullPath, download: isDownload });

			if (isDownload) {
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", file.name);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else if (newWindow) {
				newWindow.location.href = url;
			}
		} catch (e) {
			console.error("Failed to open file", e);
			if (newWindow) newWindow.close();
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Failed to open file", severity: "error" });
		}
	};

	const handleMenuOpen = (event, item) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		setSelectedItem(item);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setSelectedItem(null);
	};

	const handleShare = async () => {
		if (!selectedItem) return;
		const itemPath = selectedItem.fileName || selectedItem.fullPath;

		try {
			const url = await getVmWorkspaceSignedUrl({ fileName: itemPath, download: false });

			if (navigator.clipboard && navigator.clipboard.writeText) {
				await navigator.clipboard.writeText(url);
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: "Temporary link copied to clipboard", severity: "success" });
			} else {
				// Fallback for insecure contexts
				const textArea = document.createElement("textarea");
				textArea.value = url;
				document.body.appendChild(textArea);
				textArea.select();
				document.execCommand("copy");
				document.body.removeChild(textArea);
				setSnackBarOpen(true);
				setSnackBar({ ...snackBar, message: "Temporary link copied to clipboard", severity: "success" });
			}
		} catch (e) {
			console.error("Share failed", e);
			setSnackBarOpen(true);
			setSnackBar({ ...snackBar, message: "Failed to copy link", severity: "error" });
		}
		handleMenuClose();
	};

	const handleMoveToTrash = async (item) => {
		if (!item) return;
		const sourcePath = item.type === "folder" ? `${item.fullPath}/` : item.fullPath;
		// Move to TRASH root with the same name
		const destinationPath = item.type === "folder" ? `TRASH/${item.name}/` : `TRASH/${item.name}`;

		setLoadingOpen(true);
		try {
			await axios.post("/api/vm_workspace/move_item", {
				sourcePath,
				destinationPath,
			});
			setSnackBar({ ...snackBar, message: "Moved to Bin", severity: "success" });
			setSnackBarOpen(true);
			if (item.type === "folder") {
				setFolderItems((prev) => prev.filter((f) => f.fullPath !== item.fullPath));
			} else {
				setObjectItems((prev) => prev.filter((f) => f.fileName !== item.fileName));
			}
			await fetchInitialContents();
		} catch (e) {
			console.error("Move to trash failed", e);
			setSnackBar({ ...snackBar, message: "Failed to move to Bin", severity: "error" });
			setSnackBarOpen(true);
		} finally {
			setLoadingOpen(false);
			handleMenuClose();
		}
	};

	const handleConfirmationDialogClose = () => {
		setConfirmationDialogOpen(false);
		setBlockedTx(null);
	};

	const handleConfirmationDialogProceed = () => {
		if (blockedTx) {
			blockedTx.retry();
		}
		handleConfirmationDialogClose();
	};

	const getFileIcon = (file) => {
		if (file.type === "folder") return <FolderIcon sx={{ fontSize: 60, color: "#FFC107" }} />;
		if (file.mimeType?.startsWith("image/")) {
			return <PreviewImage src={file.url || "https://via.placeholder.com/150"} alt={file.name} />;
		}
		if (file.mimeType?.startsWith("video/")) return <MovieIcon sx={{ fontSize: 60, color: "#F44336" }} />;
		return <DescriptionIcon sx={{ fontSize: 60, color: "#2196F3" }} />;
	};

	const files = [...folderItems, ...objectItems];
	const filteredFiles = files;
	// Fallback keeps pagination usable even if backend flags are intermittently missing.
	const canLoadMore = !isSearchMode && Boolean(hasNextPage || nextCursor || objectItems.length >= DEFAULT_PAGE_SIZE);

	return (
		<Box sx={{ p: 3, height: "100vh", display: "flex", flexDirection: "column", overflowY: "auto" }}>
			{/* Header / Toolbar */}
			<Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
				<Button variant="contained" onClick={handleGoBack} startIcon={<ArrowBackRounded />} size="small">
					Back
				</Button>
				<Typography variant="h5" component="div" sx={{ flexGrow: 1 }}>
					VM Workspace
				</Typography>
				<Paper component="form" sx={{ p: "2px 4px", display: "flex", alignItems: "center", width: 300 }} elevation={1}>
					<InputBase
						sx={{ ml: 1, flex: 1 }}
						placeholder="Search"
						inputProps={{ "aria-label": "search" }}
						value={searchQuery}
						onChange={async (e) => {
							const nextValue = e.target.value;
							setSearchQuery(nextValue);
							if (!nextValue.trim() && isSearchMode) {
								await clearSearchMode();
							}
						}}
						onKeyDown={async (e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								await handleSearchSubmit();
							}
						}}
					/>
					<IconButton type="button" sx={{ p: "10px" }} aria-label="search" onClick={handleSearchSubmit}>
						<SearchIcon />
					</IconButton>
				</Paper>
				{isSearchMode && (
					<Typography variant="body2" color="text.secondary">
						{isSearching ? "Searching..." : `${totalMatches} matches`}
					</Typography>
				)}
				<Button variant="contained" startIcon={<UploadFile />} onClick={() => fileInputRef.current.click()}>
					Upload File
				</Button>
				<Button variant="outlined" startIcon={<DriveFolderUpload />} onClick={() => folderInputRef.current.click()}>
					Upload Folder
				</Button>
				<Button variant="outlined" startIcon={<CreateNewFolder />} onClick={() => setCreateFolderDialogOpen(true)}>
					New Folder
				</Button>
			</Stack>

			{/* Hidden Inputs */}
			<input
				type="file"
				multiple
				ref={fileInputRef}
				style={{ display: "none" }}
				onChange={(e) => handleUploadFiles(e.target.files)}
			/>
			<input
				type="file"
				webkitdirectory=""
				mozdirectory=""
				ref={folderInputRef}
				style={{ display: "none" }}
				onChange={(e) => handleUploadFiles(e.target.files)}
			/>

			{/* Progress Bar */}
			{uploading && (
				<Box sx={{ width: "100%", mb: 2 }}>
					<LinearProgress variant="determinate" value={uploadProgress} />
					<Box display="flex" justifyContent="space-between" mt={0.5}>
						<Typography variant="caption" color="text.secondary">
							{uploadStats.currentFile ? `Uploading: ${uploadStats.currentFile}` : "Uploading..."} {uploadProgress}%
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{formatBytes(uploadStats.uploaded)} / {formatBytes(uploadStats.total)} ({formatBytes(uploadStats.speed)}/s)
						</Typography>
					</Box>
				</Box>
			)}

			{/* Breadcrumbs */}
			<Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
				<Link
					underline="hover"
					color="inherit"
					display="flex"
					alignItems="center"
					onClick={handleHomeClick}
					sx={{ cursor: "pointer" }}>
					<HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
					Home
				</Link>
				{path.map((folder, index) => (
					<Link
						key={index}
						underline="hover"
						color={index === path.length - 1 ? "text.primary" : "inherit"}
						onClick={() => handleBreadcrumbClick(index)}
						sx={{ cursor: "pointer" }}>
						{folder}
					</Link>
				))}
			</Breadcrumbs>

			{/* Drop Zone & File Grid */}
			<DropZone isDragOver={dragOver} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
				{filteredFiles.length === 0 ? (
					<Box display="flex" alignItems="center" justifyContent="center" height="100%">
						<Typography variant="h6" color="text.secondary">
							{files.length === 0 ? "Drag and drop files here or use the upload buttons" : "No results found"}
						</Typography>
					</Box>
				) : (
					<Grid container spacing={1}>
						{filteredFiles.map((file) => (
							<Grid item xs={6} sm={4} md={3} lg={2} key={file.id}>
								<FileCard
									elevation={1}
									onClick={() => handleFileClick(file)}
									draggable
									onDragStart={(e) => {
										e.dataTransfer.setData("application/json", JSON.stringify(file));
									}}>
									<Box
										className="more-actions"
										sx={{ position: "absolute", top: 5, right: 5, opacity: 0, transition: "opacity 0.2s" }}>
										<IconButton size="small" onClick={(e) => handleMenuOpen(e, file)}>
											<MoreVertIcon fontSize="small" />
										</IconButton>
									</Box>
									<PreviewBox>{getFileIcon(file)}</PreviewBox>
									<Tooltip title={file.name} arrow>
										<Typography
											variant="body2"
											noWrap
											sx={{ width: "100%", textAlign: "center", fontSize: "0.85rem", fontWeight: 500 }}>
											{file.name}
										</Typography>
									</Tooltip>
									{file.type === "file" && (
										<>
											<Typography
												variant="caption"
												color="text.secondary"
												noWrap
												sx={{ width: "100%", textAlign: "center" }}>
												{formatBytes(file.size)}
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												noWrap
												sx={{ width: "100%", textAlign: "center" }}>
												{file.timeCreated ? new Date(file.timeCreated).toLocaleDateString() : ""}
											</Typography>
										</>
									)}
								</FileCard>
							</Grid>
						))}
					</Grid>
				)}
				{canLoadMore && (
					<Box sx={{ mt: 2, display: "flex", justifyContent: "center", width: "100%" }}>
						<Button variant="outlined" onClick={handleLoadMore} disabled={isLoadingMore}>
							{isLoadingMore ? "Loading..." : "Load more"}
						</Button>
					</Box>
				)}
			</DropZone>
			{isFetchingSignedUrls && (
				<Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
					Fetching previews...
				</Typography>
			)}
			{error && (
				<Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 2 }}>
					<Typography variant="body2" color="error">
						{error}
					</Typography>
					<Button
						size="small"
						onClick={isSearchMode ? handleSearchSubmit : canLoadMore && objectItems.length > 0 ? handleLoadMore : fetchInitialContents}>
						Retry
					</Button>
				</Box>
			)}

			{/* Context Menu */}
			<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
				<MenuItem onClick={handleShare}>
					<ListItemIcon>
						<ShareIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Copy Link</ListItemText>
				</MenuItem>
				<MenuItem onClick={() => handleMoveToTrash(selectedItem)}>
					<ListItemIcon>
						<Delete fontSize="small" />
					</ListItemIcon>
					<ListItemText>Move to Bin</ListItemText>
				</MenuItem>
				<MenuItem
					onClick={() => {
						handleFileClick(selectedItem, true);
						handleMenuClose();
					}}>
					<ListItemIcon>
						<DownloadIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText>Download</ListItemText>
				</MenuItem>
			</Menu>

			<Dialog open={createFolderDialogOpen} onClose={() => setCreateFolderDialogOpen(false)}>
				<DialogTitle>Create New Folder</DialogTitle>
				<DialogContent>
					<DialogContentText>Enter the name of the new folder.</DialogContentText>
					<TextField
						autoFocus
						margin="dense"
						label="Folder Name"
						fullWidth
						variant="standard"
						value={newFolderName}
						onChange={(e) => setNewFolderName(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCreateFolderDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleCreateFolder}>Create</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={confirmationDialogOpen}
				onClose={handleConfirmationDialogClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description">
				<DialogTitle id="alert-dialog-title">{"Leave page?"}</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						Uploads are in progress. If you leave this page, the uploads will be canceled.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleConfirmationDialogClose}>Cancel</Button>
					<Button onClick={handleConfirmationDialogProceed} autoFocus>
						Proceed
					</Button>
				</DialogActions>
			</Dialog>

			{/* Recycle Bin Floating Icon */}
			<Tooltip title="Recycle Bin (Drop files here)" placement="left">
				<Fab
					color={binDragOver ? "error" : "default"}
					sx={{ position: "fixed", bottom: 30, right: 30, zIndex: 1000 }}
					onDragOver={(e) => {
						e.preventDefault();
						setBinDragOver(true);
					}}
					onDragLeave={() => setBinDragOver(false)}
					onDrop={(e) => {
						e.preventDefault();
						setBinDragOver(false);
						const itemStr = e.dataTransfer.getData("application/json");
						if (itemStr) {
							handleMoveToTrash(JSON.parse(itemStr));
						}
					}}>
					<Delete />
				</Fab>
			</Tooltip>
		</Box>
	);
};

export default VmWorkspace;
